import { newEntityId } from '../model/document';
import type { CadEntity } from '../model/entity';
import { pt } from '../model/geom';
import { mm, degToRad, type Micron } from '../model/units';

/**
 * 정적 심볼 도형 (작업지시서 P4-07)
 *
 * 소화기·비상구·콘센트처럼 **규격이 아니라 기호로 읽히는** 것들이다.
 * 파라미터로 만들 수 없고, 만들 이유도 없다 — 크기가 아니라 모양이 정보다.
 *
 * 좌표계는 파라메트릭 블록과 같다: 원점 = 외접 사각의 좌하단, 단위 µm.
 * 도면 축척과 무관하게 알아볼 수 있도록 대부분 400~900mm 안에 담는다.
 */

const id = (): string => newEntityId();

function rect(layer: string, x: Micron, y: Micron, w: Micron, h: Micron): CadEntity {
  return {
    id: id(),
    type: 'polyline',
    layer,
    closed: true,
    points: [pt(x, y), pt(x + w, y), pt(x + w, y + h), pt(x, y + h)],
  };
}

function line(layer: string, x1: Micron, y1: Micron, x2: Micron, y2: Micron): CadEntity {
  return { id: id(), type: 'line', layer, a: pt(x1, y1), b: pt(x2, y2) };
}

function circle(layer: string, cx: Micron, cy: Micron, r: Micron): CadEntity {
  return { id: id(), type: 'circle', layer, center: pt(cx, cy), radius: r };
}

function arc(
  layer: string,
  cx: Micron,
  cy: Micron,
  r: Micron,
  startDeg: number,
  endDeg: number
): CadEntity {
  return {
    id: id(),
    type: 'arc',
    layer,
    center: pt(cx, cy),
    radius: r,
    startAngle: degToRad(startDeg),
    endAngle: degToRad(endDeg),
  };
}

function label(layer: string, cx: Micron, cy: Micron, text: string, hMm = 150): CadEntity {
  return {
    id: id(),
    type: 'text',
    layer,
    at: pt(cx, cy),
    text,
    height: mm(hMm),
    rotation: 0,
    align: 'center',
  };
}

/* -------------------------------------------------------------------------- *
 * 소방·안전
 * -------------------------------------------------------------------------- */

const FIRE = 'M-FIRE';

/** 소화기 — 원 + FE */
export function fireExtinguisher(): CadEntity[] {
  const r = mm(200);
  return [circle(FIRE, r, r, r), label(FIRE, r, mm(140), 'FE', 160)];
}

/** 옥내 소화전 — 사각 함 + 내부 원 */
export function fireHydrant(): CadEntity[] {
  const w = mm(800);
  const d = mm(250);
  return [
    rect(FIRE, 0, 0, w, d),
    circle(FIRE, Math.round(w / 2), Math.round(d / 2), mm(90)),
    label(FIRE, Math.round(w / 2), mm(300), '소화전', 150),
  ];
}

/** 스프링클러 헤드 — 작은 원 + 십자 */
export function sprinkler(): CadEntity[] {
  const r = mm(100);
  return [
    circle(FIRE, r, r, r),
    line(FIRE, 0, r, r * 2, r),
    line(FIRE, r, 0, r, r * 2),
  ];
}

/** 비상구 유도등 — 사각 + 진행 화살표 */
export function emergencyExit(): CadEntity[] {
  const w = mm(600);
  const h = mm(300);
  const my = Math.round(h / 2);
  return [
    rect(FIRE, 0, 0, w, h),
    line(FIRE, mm(100), my, mm(500), my),
    line(FIRE, mm(500), my, mm(380), my + mm(90)),
    line(FIRE, mm(500), my, mm(380), my - mm(90)),
  ];
}

/** 자동심장충격기 */
export function aed(): CadEntity[] {
  const w = mm(400);
  const h = mm(400);
  return [rect(FIRE, 0, 0, w, h), label(FIRE, Math.round(w / 2), mm(140), 'AED', 140)];
}

/* -------------------------------------------------------------------------- *
 * 전기·설비
 * -------------------------------------------------------------------------- */

const ELEC = 'M-ELEC';

/** 분전반 */
export function electricPanel(): CadEntity[] {
  const w = mm(800);
  const d = mm(250);
  return [
    rect(ELEC, 0, 0, w, d),
    line(ELEC, 0, 0, w, d),
    label(ELEC, Math.round(w / 2), mm(320), '분전반', 150),
  ];
}

/** 콘센트 — 반원 + 극 표시 */
export function outlet(): CadEntity[] {
  const r = mm(150);
  return [
    arc(ELEC, r, 0, r, 0, 180),
    line(ELEC, 0, 0, r * 2, 0),
    line(ELEC, r, r, r, r + mm(120)),
  ];
}

/** CCTV — 몸체 + 시야각 */
export function cctv(): CadEntity[] {
  const w = mm(300);
  const d = mm(200);
  return [
    rect(ELEC, 0, 0, w, d),
    line(ELEC, Math.round(w / 2), d, mm(-200), mm(900)),
    line(ELEC, Math.round(w / 2), d, w + mm(200), mm(900)),
  ];
}

/* -------------------------------------------------------------------------- *
 * 건축
 * -------------------------------------------------------------------------- */

const DOOR = 'A-DOOR';

/**
 * 여닫이문 — 문짝 + 열림 궤적.
 * 궤적이 있어야 문 앞에 매대를 놓았을 때 간섭이 눈에 보인다.
 */
export function doorSwing(): CadEntity[] {
  const w = mm(900);
  return [
    line(DOOR, 0, 0, 0, w), // 문짝 (열린 상태)
    arc(DOOR, 0, 0, w, 0, 90),
    line(DOOR, 0, 0, w, 0), // 개구부
  ];
}

/** 자동문(미닫이) — 개구부 + 양쪽 슬라이드 방향 */
export function doorSlide(): CadEntity[] {
  const w = mm(1800);
  const t = mm(100);
  const half = Math.round(w / 2);
  return [
    line(DOOR, 0, 0, w, 0),
    rect(DOOR, 0, 0, half, t),
    rect(DOOR, half, 0, half, t),
    line(DOOR, mm(200), mm(300), mm(600), mm(300)),
    line(DOOR, w - mm(200), mm(300), w - mm(600), mm(300)),
  ];
}

/** 엘리베이터 — 카 + 대각선 + 문 */
export function elevator(): CadEntity[] {
  const w = mm(2000);
  const d = mm(2000);
  return [
    rect('A-WALL', 0, 0, w, d),
    line('A-WALL', 0, 0, w, d),
    line('A-WALL', 0, d, w, 0),
    line(DOOR, mm(500), 0, mm(1500), 0),
  ];
}

/* -------------------------------------------------------------------------- *
 * 등록
 * -------------------------------------------------------------------------- */

/** 정적 심볼 생성기 — 카탈로그가 코드로 찾아 쓴다 */
export const SYMBOLS: Record<string, () => CadEntity[]> = {
  FIRE_EXT: fireExtinguisher,
  FIRE_HYDRANT: fireHydrant,
  SPRINKLER: sprinkler,
  EMERGENCY_EXIT: emergencyExit,
  AED: aed,
  ELEC_PANEL: electricPanel,
  OUTLET: outlet,
  CCTV: cctv,
  DOOR_SWING: doorSwing,
  DOOR_SLIDE: doorSlide,
  ELEVATOR: elevator,
};
